'use client';

import React, { useEffect, useState } from 'react';
import {
  IBlogResponse, IConsultation, IPostInfo, IPostsListResponse,
} from '@sapientpro/sapientpro-data-models';
import classnames from 'classnames';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import client from '../../apiClient';
import AnimatedTitle from '../../components/AnimatedTitle';
import Button, { ButtonVariants } from '../../components/Button';
import Img from '../../components/Img';
import PostFilters from '../../components/PostFilters';
import deviceScreenSize, { ScreenSize } from '../../store/deviceScreenSize';
import { theme, Theme } from '../../store/theme';
import Consultation from '../Home/Consultation';
import ArticleCard from './ArticleCard';
import AuthorCard from './AuthorCard';
import styles from './Blog.module.scss';
import Chip from './Chip';

type BlogProps = {
  data: IBlogResponse,
  posts: IPostsListResponse,
  limit: number,
};

type GetBlogPostsParams = {
  offset?: number,
  limit?: number,
  category?: string,
  tag?: string,
};
const getBlogPosts = async (params: { searchParams: GetBlogPostsParams }) => {
  try {
    return await client.get<IPostsListResponse>(
      `posts?us=true&${new URLSearchParams(JSON.parse(JSON.stringify(params.searchParams)))}`,
    );
  } catch (e) {
    console.error(e.message);
  }
};

const Blog = ({ data, posts, limit }: BlogProps) => {
  const deviceSize = useAtomValue(deviceScreenSize);
  const appTheme = useAtomValue<Theme>(theme);

  const searchParams = useSearchParams();
  const filteredTag = new URLSearchParams(searchParams).get('tag');
  const filteredCategory = new URLSearchParams(searchParams).get('category');

  const [renderedPosts, setRenderedPosts] = useState<IPostInfo[]>(posts.posts);

  const [offset, setOffset] = useState<number>(0);
  const [total, setTotal] = useState<number | null>(posts.pagination.total);
  const [isLoading, setIsLoading] = useState(false);

  const loadPosts = async (params = {}, clearOffset = false) => {
    try {
      setIsLoading(true);
      const loadedPosts = await getBlogPosts({
        searchParams: JSON.parse(JSON.stringify({
          offset: clearOffset ? 0 : offset + limit,
          limit,
          tag: filteredTag || undefined,
          category: filteredCategory || undefined,
          ...params,
        })),
      });
      setOffset(clearOffset ? 0 : offset + limit);
      setTotal(loadedPosts?.pagination?.total || null);
      setRenderedPosts(prev => (clearOffset ? (loadedPosts?.posts || []) : [...prev, ...(loadedPosts?.posts || [])]));
    } catch (e) {
      console.error(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPosts({ tag: filteredTag || undefined, category: filteredCategory || undefined }, true);
  }, [filteredTag, filteredCategory]);

  return (
    <main className={styles.blog}>
      <div className='container'>
        <article className={styles.bannerArticle}>
          <div className={styles.bannerArticle__headline}>
            <AuthorCard
              url={data.banner.author.slug && `/blog/author/${data.banner.author.slug}`}
              className={styles.bannerAuthor}
              name={data.banner.author.pseudonym}
              avatar={data.banner.author.avatar?.url}
              avatarSize={deviceSize === ScreenSize.DESKTOP_HUGE ? 76 : 48}
              info={[data.banner.publishDate, `${data.banner.readTime} min read`]}
              verticalLayout={
                deviceSize === ScreenSize.MOBILE
              }
            />
            <h2 className={styles.bannerArticle__title}>
              <Link href={`/blog/${data.banner.slug}`}>
                <AnimatedTitle title={data.banner.title} />
              </Link>
            </h2>
          </div>

          <Link
            href={`/blog/${data.banner.slug}`}
            className={styles.bannerArticle__image}
          >
            <Img
              src={data.banner.backgroundImage.url}
              alt={data.banner.backgroundImage.alternativeText}
              withPreloader
            />
          </Link>
        </article>

        <section className={styles.blog__posts}>
          <div className={styles.blog__mainList}>
            <PostFilters
              className={styles.blog__categories}
              filters={data.categories}
              activeFilter={filteredCategory || undefined}
              clearFilter='All'
              useLinks
              linkPrefix={{
                clear: filteredTag ? `/blog?tag=${filteredTag}` : '/blog',
                full: filteredTag ? `/blog?tag=${filteredTag}&category=` : '/blog?category=',
              }}
            />
            {renderedPosts?.map(article => (
              <ArticleCard
                className={styles.blog__mainListCard}
                data={article}
                key={article.id}
                filteredTag={filteredTag || undefined}
                tagScroll={false}
              />
            ))}
            {(total === null || total > renderedPosts.length) && (
              <Button
                variant={ButtonVariants.OUTLINED}
                className={styles.loadMoreButton}
                onClick={() => loadPosts()}
                icon={isLoading && (
                  <img
                    src={`/media/spinner_${appTheme}.webp`}
                    alt=''
                  />
                )}
                iconSize={{ width: 24, height: 24 }}
              >
                Load more
              </Button>
            )}
          </div>

          <aside className={styles.blog__sidebar}>
            <section className={styles.blog__popularTopics}>
              <h3 className={classnames('subtitle', styles.blog__popularTopicsTitle)}>Popular topics:</h3>
              <div className={styles.blog__chipsContainer}>
                <Chip
                  url={filteredCategory ? `/blog?category=${filteredCategory}` : '/blog'}
                  scroll={false}
                  active={!searchParams.get('tag')}
                  title='All topics'
                />
                {data.tags.map(chip => (
                  <Chip
                    url={filteredCategory ? `/blog?category=${filteredCategory}&tag=${chip.slug}` : `/blog?tag=${chip.slug}`}
                    scroll={false}
                    active={searchParams.get('tag') === chip.slug}
                    title={chip.title}
                    key={chip.title}
                  />
                ))}
              </div>
            </section>
            {/* <section className={styles.blog__knowledgeBaseArticles}> */}
            {/*  <h3 className={classnames('subtitle', styles.blog__knowledgeTitle)}>Knowledge base:</h3> */}
            {/*  <div className={styles.blog__knowledgeContent}> */}
            {/*    {data.knowledgeBase.map(article => ( */}
            {/*      <ArticleCard */}
            {/*        className={styles.blog__knowledgeArticleCard} */}
            {/*        data={article} */}
            {/*        key={article.id} */}
            {/*        withoutDescription */}
            {/*        verticalLayout */}
            {/*      /> */}
            {/*    ))} */}
            {/*  </div> */}
            {/* </section> */}

            {/* <FollowBlock /> */}
          </aside>
        </section>

        <section className={styles.exploreTrending}>
          <header className={styles.exploreTrendingHeader}>
            <h3 className={styles.exploreTrendingTitle}>
              Explore trending
            </h3>
          </header>

          <div className={styles.trendingArticles}>
            {data.trendingPosts.map((trendingArticle, index) => (
              <article
                className={styles.trendingArticle}
                key={trendingArticle.id}
              >
                <Link
                  href={`/blog/${trendingArticle.slug}`}
                  className={classnames(styles.trendingCardImage, {
                    [styles.bigOne]: index === 0,
                  })}
                >
                  <Img
                    src={trendingArticle.backgroundImage.url}
                    alt={trendingArticle.backgroundImage.alternativeText || ''}
                    withPreloader
                  />
                </Link>
                <Link
                  href={`/blog/${trendingArticle.slug}`}
                  className={styles.trendingCardTitle}
                >
                  <AnimatedTitle
                    title={trendingArticle.title}
                  />
                </Link>
              </article>
            ))}

          </div>
        </section>
      </div>

      <Consultation
        className={styles.section}
        data={{
          title: 'Subscribe to our **newsletter!**',
          description: 'Talk to our experts to get an estimate and build a market-winning solution for your business.',
        } as IConsultation}
      />
    </main>
  );
};

export default Blog;
